import { Form, Formik } from "formik"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"
import * as Yup from "yup"
import Button from "../../../shared/components/buttons/Button"
import { StyleVariants } from "../../../shared/state/sharedTypes"
import { IUserChangePasswordData } from "../../user/state/userTypes"
import Label from "../../../shared/components/forms/Label"
import InputField from "../../../shared/components/forms/InputField"
import FieldError from "../../../shared/components/forms/FieldError"
import { changePasswordAction } from "../../user/state/userActions"
import { IAppState } from "../../../app/appTypes"
import { getUserByHasUserId } from "../../../shared/selectors/user"
import Card from "../../../shared/components/layout/Card"
import ButtonExpandCollapse from "../../../shared/components/buttons/ButtonExpandCollapse"
import { setPasswordValidationSchema } from "../../account/state/setPasswordValidation"

interface IProps {
  userId: string
}

const UserPasswordEditForm: React.FC<IProps> = (props: IProps): JSX.Element => {
  const { t } = useTranslation(["user", "shared"])
  const [isVisible, toggleVisible] = useState(false)
  const user = useSelector((state: IAppState) => getUserByHasUserId(state, props))

  const dispatch = useDispatch()
  const changePassword = (userId: string, data: IUserChangePasswordData) => {
    dispatch(changePasswordAction(userId, data))
  }

  const initialValues: IUserChangePasswordData = {
    currentPassword: "",
    password: "",
    passwordConfirmation: ""
  }

  const validationSchema = setPasswordValidationSchema().shape({
    currentPassword: Yup.string()
      .required(t("shared:Required"))
  })

  if (!user) return null

  return (
    <div className="flex justify-left pl-4">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          changePassword(props.userId, values)
          resetForm()
          setSubmitting(false)
          toggleVisible(false)
        }}
      >
        {({
          isSubmitting,
          handleChange,
          handleBlur,
          values,
          errors,
          touched
        }) => {
          return (
            <Form className="w-full lg:w-2/3">
              <Card className="flex-col mb-6">
                <div className="flex items-center flex-wrap">
                  <h3 className="mr-4 mb-2 text-lg">
                    {t("user:changePassword")}
                  </h3>
                </div>
                <div className="flex mb-2 items-end">
                  <p className="flex-1 pr-10 lg:pr-24">
                    {/* Add description if needed */}
                  </p>
                  <ButtonExpandCollapse
                    isExpanded={isVisible}
                    action={() => toggleVisible(!isVisible)}
                    expandText={t("shared:show")}
                    collapseText={t("shared:hide")}
                  />
                </div>

                <div className={classNames({ hidden: !isVisible })}>
                  <div className="mb-2">
                    <Label name="currentPassword">
                      {t("user:currentPassword")}
                    </Label>
                    <InputField
                      type="password"
                      name="currentPassword"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.currentPassword}
                    />
                  </div>

                  <div className="mb-2">
                    <Label name="password">
                      {t("user:password")}
                    </Label>
                    <InputField
                      type="password"
                      name="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      autoComplete="new-password"
                    />
                    <FieldError errorMessage={errors.password as string} isVisible={(errors.password && touched.password) as boolean} />
                  </div>

                  <div className="mb-6">
                    <Label name="passwordConfirmation">
                      {t("user:passwordConfirmation")}
                    </Label>
                    <InputField
                      type="password"
                      name="passwordConfirmation"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.passwordConfirmation}
                      autoComplete="new-password"
                    />
                    <FieldError errorMessage={errors.passwordConfirmation as string} isVisible={(errors.passwordConfirmation && touched.passwordConfirmation) as boolean} />
                  </div>

                  <div className="flex justify-center">
                    <Button
                      isDisabled={isSubmitting}
                      variant={StyleVariants.PURPLE}
                      isSubmit={true}
                    >
                      {t("user:updatePassword")}
                    </Button>
                  </div>
                </div>
              </Card>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default UserPasswordEditForm
